import React from 'react';
import { Card, CardContent } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { Avatar } from './ui/Avatar';
import { Calendar, MessageSquare, Flag, MoreVertical } from 'lucide-react';
export type TaskStatus = 'todo' | 'in_progress' | 'done';
export type TaskPriority = 'low' | 'medium' | 'high';
export interface Task {
  id: string;
  title: string;
  description: string;
  status: TaskStatus;
  priority: TaskPriority;
  assignee: {
    id: string;
    name: string;
    avatar: string;
  };
  project?: {
    id: string;
    name: string;
  };
  deadline: string;
  commentsCount: number;
  createdAt: string;
}
interface TaskCardProps {
  task: Task;
  onStatusChange?: (id: string, status: TaskStatus) => void;
  onClick?: () => void;
}
export function TaskCard({
  task,
  onStatusChange,
  onClick
}: TaskCardProps) {
  const statusConfig = {
    todo: {
      variant: 'secondary' as const,
      label: 'To Do',
      color: 'bg-gray-100 text-gray-700'
    },
    in_progress: {
      variant: 'default' as const,
      label: 'In Progress',
      color: 'bg-blue-100 text-blue-700'
    },
    done: {
      variant: 'success' as const,
      label: 'Done',
      color: 'bg-green-100 text-green-700'
    }
  };
  const priorityConfig = {
    low: {
      color: 'text-gray-500',
      bg: 'bg-gray-100'
    },
    medium: {
      color: 'text-yellow-600',
      bg: 'bg-yellow-100'
    },
    high: {
      color: 'text-red-600',
      bg: 'bg-red-100'
    }
  };
  const daysUntilDeadline = Math.ceil((new Date(task.deadline).getTime() - new Date().getTime()) / (1000 * 3600 * 24));
  const isOverdue = daysUntilDeadline < 0;
  const isUrgent = daysUntilDeadline >= 0 && daysUntilDeadline < 3;
  return <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onClick} data-id="element-314">
      <CardContent className="p-4" data-id="element-315">
        <div className="flex items-start justify-between mb-3" data-id="element-316">
          <div className="flex-1" data-id="element-317">
            <div className="flex items-center space-x-2 mb-2" data-id="element-318">
              <Badge variant={statusConfig[task.status].variant} className="text-xs" data-id="element-319">
                {statusConfig[task.status].label}
              </Badge>
              <div className={`flex items-center space-x-1 px-2 py-0.5 rounded-full ${priorityConfig[task.priority].bg}`} data-id="element-320">
                <Flag className={`h-3 w-3 ${priorityConfig[task.priority].color}`} data-id="element-321" />
                <span className={`text-xs font-medium capitalize ${priorityConfig[task.priority].color}`} data-id="element-322">
                  {task.priority}
                </span>
              </div>
            </div>
            <h3 className="font-semibold text-gray-900 mb-1" data-id="element-323">{task.title}</h3>
            <p className="text-sm text-gray-600 line-clamp-2" data-id="element-324">
              {task.description}
            </p>
          </div>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={e => e.stopPropagation()} data-id="element-325">
            <MoreVertical className="h-4 w-4" data-id="element-326" />
          </Button>
        </div>

        {task.project && <div className="mb-3 px-2 py-1 bg-purple-50 rounded text-xs text-purple-700 inline-block" data-id="element-327">
            {task.project.name}
          </div>}

        <div className="flex items-center justify-between text-xs text-gray-500 mb-3" data-id="element-328">
          <div className="flex items-center space-x-3" data-id="element-329">
            <div className="flex items-center" data-id="element-330">
              <Calendar className="h-3 w-3 mr-1" data-id="element-331" />
              <span className={isOverdue ? 'text-red-600 font-medium' : isUrgent ? 'text-orange-600 font-medium' : ''} data-id="element-332">
                {task.deadline}
                {isOverdue && ' (Overdue)'}
              </span>
            </div>
            <div className="flex items-center" data-id="element-333">
              <MessageSquare className="h-3 w-3 mr-1" data-id="element-334" />
              <span data-id="element-335">{task.commentsCount}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t" data-id="element-336">
          <div className="flex items-center space-x-2" data-id="element-337">
            <Avatar src={task.assignee.avatar} alt={task.assignee.name} size="sm" data-id="element-338" />
            <span className="text-sm text-gray-700" data-id="element-339">{task.assignee.name}</span>
          </div>
          {task.status !== 'done' && <select value={task.status} onChange={e => {
          e.stopPropagation();
          onStatusChange?.(task.id, e.target.value as TaskStatus);
        }} className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={e => e.stopPropagation()} data-id="element-340">
              <option value="todo" data-id="element-341">To Do</option>
              <option value="in_progress" data-id="element-342">In Progress</option>
              <option value="done" data-id="element-343">Done</option>
            </select>}
        </div>
      </CardContent>
    </Card>;
}